import { useContext, useState, useEffect } from "react";
import { ApiResponse } from "../models/api/ApiResponse";
import { getListOfQueriesBasedOnModule } from "../api/QuerySetUp";
import { getListofFilters } from "../api/QueryDesigner";
import { getSyCode30Records } from "../api/SyCodes";
import { getCodeValues } from "../api/ValidationCodes";
import { executeQuery, getQueryCriteriaData } from "../api/QueryExecution";
import { getOptionByIdWithCORP } from "../api/TabwareOptions";
import { TranslationMessagesContext } from "../util/Translations";
import { ModuleNames } from "../util/Constants";
import { getUserSetups } from "../api/UserSetups";

export interface FilterParam {
  ColumnName: string;
  TableName: string;
  TranslatedIdText: string;
  FormatString: string;
  ParameterValue: string | number | null | undefined;
  AdditionalInfo: string;
  Requirements: any[];
  MaxSize: number;
  ElementType: string;
}
export interface ExecParam {
  TableAlias: string;
  ColumnAlias: string;
  Operator: string;
  ParameterValue: string | number | null | undefined;
}
export interface CriteriaDef {
  AskAtExecutionFlag: boolean;
  CaseIndicator: boolean;
  Category: any;
  CloseParensCount: number;
  ColumnName: string;
  CompanionElement: any;
  CompanionExpression: any;
  ComparisonColumn: any;
  ComparisonExpression: string;
  ComparisonOperator: string;
  ComparisonTable: any;
  DataType: string;
  DbColumnName: string;
  IdText: string;
  IsDefault: boolean;
  JoinId: number;
  LogicalOperator: string;
  ModuleName: string;
  OpenParensCount: number;
  Plant: string;
  ProhibitEdit: boolean;
  QueryName: string;
  Sequence: number;
  TZComparisionElement: string;
  TZDatatype: number;
  TableAlias: string;
  TableName: string;
  TextCaseType: string;
  TranslatedIdText: string;
  UdCategory: any;
  UdElement: any;
  UdSubcategory: any;
  Userid: string;
  isResourceColumn: boolean;
  isSearchableCriteriaColumn: boolean;
}

export type QueryInfo = {
  queryDetails: {
    plant: string;
    moduleName: string;
    queryName: string;
    userId: string;
  };
  sqlText: string;
  sqlParameter: { name: string; value: any }[];
};
export interface CriteriaParameter {
  CriteriaSequence: number;
  TableAlias: string;
  ColumnAlias: string;
  JoinID: number;
  ComparisonExpression: string;
  CompanionElement: any;
  CompanionExpression: any;
}
export interface QueryResult {
  HeaderRow: number;
  HeaderName: string;
  HeaderType: string;
  HeaderText: string;
  HeaderValue: string;
  HeaderColor: string;
  Columns: ColumnDef[];
}
export interface ColumnDef {
  Order: number;
  Name: string;
  Text: string;
  Value: string;
  Type: string;
  Hidden: boolean;
}
export let maxResults = 100;

export interface FieldDef {
  DisplayOrder: number;
  TranslatedIdText: string;
  ColumnName: string;
  DataType: string;
  JoinId: number;
  HTMLDisplayAdditionalInfo?: { URL: string } | null;
}

export interface ColorCodes {
  Code: string;
  Color: string;
}

export async function updatePriorityCodes(
  onError: (data: any) => void,
  onSuccess: (pCodes: ColorCodes[]) => void
): Promise<ColorCodes[]> {
  let response: ApiResponse = await getSyCode30Records({
    subCategory: "Priority",
    isDataTransNeeded: true,
  });
  let pCodes: ColorCodes[] = [];
  if (response.status !== 200) {
    onError(response.data);
  } else if (response.data?.Code30UdValues != null) {
    response.data.Code30UdValues.forEach((result: any) => {
      if (result.Element === "Mobile Color" && result.StringValue !== null) {
        pCodes.push({ Code: result.Code, Color: `${result.StringValue}` });
      }
    });
    onSuccess(pCodes);
  }
  return pCodes;
}

export async function updateStatusCodes(
  onError: (data: any) => void,
  onSuccess: (pCodes: ColorCodes[]) => void
): Promise<ColorCodes[]> {
  let response: ApiResponse = await getCodeValues({
    tableName: "sy_wo_status",
    fieldName: "",
  });
  let pCodes: ColorCodes[] = [];
  if (response.status !== 200) {
    onError(response.data);
  } else if (response.data != null) {
    response.data.forEach((result: any) => {
      if (result.MobileColor !== null) {
        pCodes.push({ Code: result.Code, Color: `${result.MobileColor}` });
      }
    });
    onSuccess(pCodes);
  }
  return pCodes;
}

export async function updateNoOfFieldsToDisplay(
  option: string,
  onSuccess: (noOfFields: number) => void
) {
  const response = await getOptionByIdWithCORP(option);
  const number = response.data.PlantValue
    ? response.data.PlantValue
    : response.data.DefaultValue;
  if (number && !Number.isNaN(+number)) {
    onSuccess(+number);
  }
}

export function useQueryExec(
  moduleName: string,
  windowName: string,
  keyFieldName: string,
  loadCodes?: boolean,
  includeQwColumns?: boolean
) {
  const { translatedMessages } = useContext(TranslationMessagesContext);

  async function processError(errorData: any) {
    if (!!errorData) {
      if (errorData.includes("relogin")) {
        setErrorSate(
          translatedMessages["SessionExpired"]?.MessageText ||
            "Your session has expired. Please re-login."
        );
      } else {
        setErrorSate(errorData);
      }
    }
  }
  const [queryList, setQueryList] = useState<string[]>([]);
  const [queryUserList, setQueryUserList] = useState<
    { queryName: string; userId: string }[]
  >([]);

  async function getQueryList() {
    let response: ApiResponse = await getListOfQueriesBasedOnModule({
      moduleName: moduleName,
      forQueryListing: true,
      forQueryExecution: false,
      includeQwColumns: includeQwColumns ?? false,
    });
    const qryNames: string[] = [];
    const qryUserNames: { queryName: string; userId: string }[] = [];
    if (response.status !== 200) {
      processError(response.data);
    } else if (!!response.data) {
      response.data.QueryList.forEach((qry: { QueryName: string }) => {
        qryNames.push(qry.QueryName);
      });
      response.data.QueryList.forEach(
        (qry: { QueryName: string; Userid: string }) => {
          qryUserNames.push({ queryName: qry.QueryName, userId: qry.Userid });
        }
      );
    }
    setQueryList(qryNames);
    setQueryUserList(qryUserNames);
  }

  const [filterParams, setFilterParams] = useState<FilterParam[]>([]);

  async function getFilterParams() {
    const listParams: FilterParam[] = [];
    let response: ApiResponse = await getListofFilters({
      includeValidValues: false,
      moduleName: moduleName,
      windowName: windowName,
    });
    if (response.status !== 200) {
      processError(response.data);
    } else if (response.data != null) {
      response.data.forEach((result: FilterParam) => {
        result.ParameterValue = "";
        listParams.push(result);
      });
    }
    setFilterParams(listParams);
  }

  async function setParameter(
    paramKey: string,
    paramValue: string | number | null | undefined
  ) {
    if (filterParams) {
      const listParams = filterParams;
      const res = listParams.find((p) => {
        return p.ColumnName === paramKey;
      });
      if (res != null) res.ParameterValue = paramValue;
      setFilterParams(listParams);
    }
  }

  const [priorityCodes, setPriorityCodes] = useState<ColorCodes[]>([]);
  async function getPriorityCodes() {
    return await updatePriorityCodes(processError, setPriorityCodes);
  }
  const [statusCodes, setStatusCodes] = useState<ColorCodes[]>([]);
  async function getStatusCodes() {
    return await updateStatusCodes(processError, setStatusCodes);
  }

  const [queryCriteria, setQueryCriteria] = useState<CriteriaDef[]>([]);
  async function getQueryCriteria(queryName: string) {
    const queryUser = queryUserList.find((q) => q.queryName === queryName);
    setShowLoading(true);
    let response: ApiResponse = await getQueryCriteriaData({
      QueryName: queryName,
      ModuleName: moduleName,
      IsPublicQuery: queryUser?.userId == "Public Query",
      DefaultQuery: false,
    });
    const newCriteria: CriteriaDef[] = [];
    if (response.status !== 200) {
      processError(response.data);
    } else if (response.data.QwCriteria) {
      response.data.QwCriteria.forEach((criteria: CriteriaDef) => {
        if (criteria.AskAtExecutionFlag) {
          newCriteria.push(criteria);
        }
      });
    }
    setQueryCriteria(newCriteria);
    setShowLoading(false);
  }

  const [queryRows, setQueryRows] = useState<QueryResult[]>([]);
  const [filterRows, setFilterRows] = useState<QueryResult[]>([]);
  const [resultsFilter, setResultsFilter] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [errorState, setErrorSate] = useState("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [queryInfo, setQueryInfo] = useState<QueryInfo | undefined>(undefined);

  useEffect(() => {
    getSavedRowsToShow();
  }, []);

  const getSavedRowsToShow = async () => {
    const httpParams = {
      userSection: "QuerySettings",
      userParameter: "rows_to_show",
    };
    const response = await getUserSetups(httpParams);
    const savedValue = response.data?.UserValue;
    if (savedValue && !isNaN(Number(savedValue))) {
      maxResults = Number(savedValue);
    }
  };

  async function executeSearch(
    queryName: string,
    parameters?: CriteriaParameter[]
  ) {
    setErrorSate("");
    setShowLoading(true);
    setQueryRows([]);
    setResultsFilter("");
    let fields: FieldDef[] = [];
    const execParams: ExecParam[] = [];
    if (filterParams != null) {
      filterParams.forEach((p) => {
        if (p.ParameterValue !== "")
          execParams.push({
            TableAlias: p.TableName,
            ColumnAlias: p.ColumnName,
            Operator: p.AdditionalInfo,
            ParameterValue: p.ParameterValue,
          });
      });
    }
    let finalQueryResult = [];
    const queryUser = queryUserList.find((q) => q.queryName === queryName);
    let isPublicQuery = queryUser?.userId === "Public Query";
    const publicQueries = [
      "My Recent WOs",
      "My Assigned Work Orders",
      "Weekly Time Entries",
      "Mobile Approvals",
      "My Inspections",
      "SYS Open WOs for Equipment",
    ];

    if (publicQueries.includes(queryName)) {
      isPublicQuery = true;
    }
    if (queryName === "Weekly Time Entries") {
      moduleName = "Labor Activity";
    }
    // Fetch priority/status color codes in parallel with the query itself.
    // Previously these awaited sequentially after executeQuery, adding two
    // serial round-trips before results were rendered (AB#858840).
    const shouldLoadCodes =
      !!loadCodes &&
      priorityCodes.length === 0 &&
      statusCodes.length === 0;
    const codesPromise: Promise<[ColorCodes[], ColorCodes[]]> = shouldLoadCodes
      ? Promise.all([getPriorityCodes(), getStatusCodes()])
      : Promise.resolve([priorityCodes, statusCodes]);
    
    let response: ApiResponse = await executeQuery({
      QueryName: queryName,
      ModuleName: moduleName,
      EnableMultiPlantExecution: false,
      IsPublicQuery: isPublicQuery,
      FilterParameters: execParams,
      AskAtExecutionCriterias: parameters,
    });
    if (response.status !== 200) {
      setErrorSate(response.data);
    } else {
      let counter = 0;
      let queryData: QueryResult[] = [];
      const data: any = response.data;
      if (!data?.Success) {
        processError(data.Messages[0].Text);
      }
      if (response.data?.Success) {
        setTotalCount(Number.parseInt(response.data.Count.toString()));
        const { QueryName, ModuleName, Plant, Userid } =
          response.data.QueryData;
        setQueryInfo({
          queryDetails: {
            plant: Plant,
            moduleName: ModuleName,
            queryName: QueryName,
            userId: Userid,
          },
          sqlText: response.data.ExecutionSql,
          sqlParameter: response.data.QueryParameters.map((p: any) => ({
            name: p.Name,
            value: p.Value,
          })),
        });
        if (response.data?.QueryData?.QwColumn) {
          response.data.QueryData.QwColumn.forEach((field: FieldDef) => {
            fields.push(field);
          });
        }
        // Dynamically detect the key column from query metadata
        // Look for the column whose hyperlink navigates to a work order
        const hyperlinkCol = fields.find(
          (col: FieldDef) =>
            col.HTMLDisplayAdditionalInfo?.URL?.includes("WorkManagement")
        );
        const resolvedKeyFieldName = hyperlinkCol
          ? hyperlinkCol.ColumnName
          : keyFieldName;
        
        finalQueryResult = response.data.QueryResult;
        if (
          queryName === "Weekly Time Entries" &&
          moduleName === "Labor Activity"
        ) {
          // Use QwColumn metadata to find the actual result column keys
          const findCol = (colName: string) => {
            const field = fields.find((f) => f.ColumnName === colName);
            return field ? `${field.ColumnName}_${field.JoinId}` : undefined;
          };
          const woCol = findCol(resolvedKeyFieldName);
          const empCol = findCol("employee_id");
          const dateCol = findCol("work_date");
          const resourceCol = findCol("resource_id");
          const rateCol = findCol("rate_name");
          const hoursCol = findCol("work_hours");

          // Validate that all required column keys could be resolved before reducing
          if (!woCol || !empCol || !dateCol || !hoursCol) {
            finalQueryResult = response.data.QueryResult;
          } else {
            const reducedQueryResult = response.data.QueryResult.reduce(
              (acc: any, curr: any) => {
                // Find an item in the accumulator that matches the current item (excluding work hours)
                const found = acc.find(
                  (item: any) =>
                    item[empCol] === curr[empCol] &&
                    item[woCol] === curr[woCol] &&
                    item[dateCol] === curr[dateCol] &&
                    (!resourceCol || item[resourceCol] === curr[resourceCol]) &&
                    (!rateCol || item[rateCol] === curr[rateCol])
                );

                if (found) {
                  // If a matching item is found, add the current item's work hours to it
                  found[hoursCol] += curr[hoursCol];
                } else {
                  // If no matching item is found, add the current item to the accumulator
                  acc.push({ ...curr });
                }

                return acc;
              },
              []
            );
            //remove any row with work_hours 0
            finalQueryResult = reducedQueryResult.filter(
              (item: any) => item[hoursCol] !== 0
            );
          }
        }
        finalQueryResult.forEach((res: any) => {
          queryData.push({
            HeaderRow: counter,
            HeaderName: resolvedKeyFieldName,
            HeaderText: resolvedKeyFieldName,
            HeaderType: "String",
            HeaderValue: "",
            HeaderColor: "",
            Columns: [],
          });
          let cols = Object.entries(res);
          cols.forEach((col, index) => {
            //get the label from field def
            let field = fields.find((f) => {
              return col[0] === `${f.ColumnName}_${f.JoinId}`;
            });
            queryData[counter].Columns.push({
              Order: field !== undefined ? field.DisplayOrder : index,
              Name: col[0],
              Text: field !== undefined ? field.TranslatedIdText : col[0],
              Value: col[1] as string,
              Type: field !== undefined ? field.DataType : "String",
              Hidden: false,
            });
            if (field !== undefined && field.ColumnName === resolvedKeyFieldName) {
              queryData[counter].HeaderText = field.TranslatedIdText;
              queryData[counter].HeaderValue = col[1] as string;
              queryData[counter].Columns[index].Hidden = true;
            }
          });
          counter++;
        });
        let pf = fields.find((f) => {
          return f.ColumnName === "priority";
        });
        let sf = fields.find((f) => {
          return f.ColumnName === "status";
        });
        const [pc, sc] = await codesPromise;
        if (pc.length > 0 && pf) {
          queryData.forEach((r) => {
            let pCode = r.Columns.find((c) => {
              return c.Name === `${pf?.ColumnName}_${pf?.JoinId}`;
            });
            r.HeaderColor =
              pc.find((c) => {
                return c.Code === pCode?.Value;
              })?.Color ?? "";
          });
        } else if (sc.length > 0 && sf) {
          queryData.forEach((r) => {
            let pCode = r.Columns.find((c) => {
              return c.Name === `${sf?.ColumnName}_${sf?.JoinId}`;
            });
            r.HeaderColor =
              sc.find((c) => {
                return c.Code === pCode?.Value;
              })?.Color ?? "";
          });
        }
      }
      if (response.data.QueryData && response.data.QueryData.QueryName === "Weekly Time Entries") {
        noOfFieldsToDisplay = fields.length;
      }
      if (noOfFieldsToDisplay) {
        for (const item of queryData) {
          item.Columns.sort((a, b) => {
            return a.Order - b.Order;
          }); //sort explicitly for iOS bug
          item.Columns = item.Columns.slice(0, noOfFieldsToDisplay + 1);
        }
      }
      setQueryRows(queryData);
      setFilterRows(queryData.slice(0, maxResults));
    } //else
    setShowLoading(false);
  }

  async function execFilter(queryString: string) {
    if (queryString) {
      // Exact substring match (case-insensitive) across all column values.
      // Replaces Fuse.js fuzzy matching, which returned near-miss values
      // (e.g. "ALEQ1" also matched "ALEP1"/"ALEQP") and whose
      // minMatchCharLength guard made single-character filtering depend on
      // consecutive repeated characters rather than plain containment.
      const needle = queryString.toLowerCase();
      const res = queryRows.filter((row) =>
        row.Columns.some((col) =>
          String(col.Value ?? "").toLowerCase().includes(needle)
        )
      );
      setFilterRows(res.slice(0, maxResults));
    } else {
      setFilterRows(queryRows.slice(0, maxResults));
    }
    setResultsFilter(queryString);
  }

  let [noOfFieldsToDisplay, setNoOfFieldsToDisplay] = useState<number>();
  // Re-slice columns if noOfFieldsToDisplay arrives after rows are rendered.
  // executeSearch slices in-line when the limit is already known; this handles
  // the race where the option fetch races executeQuery (AB#858840).
  useEffect(() => {
    if (!noOfFieldsToDisplay || queryRows.length === 0) return;
    const limit = noOfFieldsToDisplay;
    const needsResize = queryRows.some(
      (r) => r.Columns.length > limit + 1
    );
    if (!needsResize) return;
    const reslice = (rows: QueryResult[]) =>
      rows.map((r) => ({
        ...r,
        Columns: [...r.Columns]
          .sort((a, b) => a.Order - b.Order)
          .slice(0, limit + 1),
      }));
    setQueryRows(reslice);
    setFilterRows(reslice);
  }, [noOfFieldsToDisplay, queryRows]);
  
  async function getNoOfFieldsToDisplay(option: string) {
    if (noOfFieldsToDisplay !== undefined) {
      return;
    }
    if (moduleName === ModuleNames.Item) {
      option = "InvNoOfFieldsToDisplay";
    }
    await updateNoOfFieldsToDisplay(option, setNoOfFieldsToDisplay);
  }
  const [searchYn, setSearchYn] = useState(false);
  const [queryName, setQueryName] = useState("");
  const [criteriaParams, setCriteriaParams] = useState<CriteriaParameter[]>();
  async function confirmSearch(
    currentQuery: string,
    filterParams?: FilterParam[],
    parameters?: CriteriaParameter[]
  ) {
    setCriteriaParams(parameters);
    setQueryName(currentQuery);
    setSearchYn(true);
  }

  return {
    getQueryList,
    queryList,
    getFilterParams,
    filterParams,
    setParameter,
    showLoading,
    setShowLoading,
    showToast,
    setShowToast,
    executeSearch,
    execFilter,
    queryRows,
    filterRows,
    resultsFilter,
    setQueryRows,
    getPriorityCodes,
    getStatusCodes,
    errorState,
    totalCount,
    queryCriteria,
    getQueryCriteria,
    noOfFieldsToDisplay,
    getNoOfFieldsToDisplay,
    searchYn,
    setSearchYn,
    queryName,
    setQueryName,
    criteriaParams,
    confirmSearch,
    queryInfo,
  };
}
